import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MaterialReactTable } from "material-react-table";

import * as url from "helper/UrlHelper";
import { getData, postData } from "utils/Gateway";

import AutomationEventsHeader from "../AutomationEventsHeader/AutomationEventsHeader";
import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import AddLabelModal from "components/Common/Modal/AddLabelModal";

import {automationHeaderLinks} from "helper/AutomationHelper/AutomationMenuLinks";

import ViewEventsPopup from "../Popup/ViewEventsPopup";
import EventFilterPopup from "../Popup/EventFilterPopup";

import typeOptions from "data/Automation/Type.json";
import actionOptions from "data/Automation/Actions.json";
import { assetImages } from "constants";

const AutomationEventsListBody = () => {

  const token = localStorage.getItem("token");

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [eventsList, setEventsList] = useState([]);

  const [selectedEventId, setSelectedEventId] = useState(null); // when click single event
  const [selectedEventIds, setSelectedEventIds] = useState([]); // when select multiple events
  const [rowSelection, setRowSelection] = useState({}); // to manage row selection state

  const [reloadData, setReloadData] = useState(false);

  const commonHeaderObject = automationHeaderLinks(t);

  const [jsonFilterQuery, setJsonFilterQuery] = useState({});
  const [isFilterReset, setIsFilterReset] = useState(false);
  const [isBulkStopping, setIsBulkStopping] = useState(false);

   //* alert requirements
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [messageType, setMessageType] = useState("");


  // Fetch automation events in process
  const fetchAutomationEvents = async () => {

    setSelectedEventIds([]);
    setRowSelection({});

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      let requestUrl = `${url.API_BASE_URL}${url.API_AUTOMATION_EVENTS}`;

      if (jsonFilterQuery.length > 0) {
        // Sanitize the jsonFilterQuery
        const sanitizedQuery = jsonFilterQuery.map((filter) => {
          if (
            (filter.operator === "includes" ||
              filter.operator === "excludes") &&
            Array.isArray(filter.value)
          ) {
            return {
              ...filter,
              value: filter.value.map((item) => item.value).join(","), // Extract and join values as a comma-separated string
            };
          }
          return filter;
        });

        requestUrl = requestUrl + `&filters=${JSON.stringify(sanitizedQuery)}`;
      }

      console.log("Fetching Automation Events from URL:", requestUrl); // Debug log

      const response = await getData(requestUrl);

      console.log("Automation Events Response:", response);

      if (response.status) {
        setEventsList(response.data);
      } else {
        console.error("Error fetching automation events:", response.message);
      }
    } catch (error) {
      console.error("Error fetching automation events:", error);
    } finally {
      setIsLoading(false);
    }
  };

  //** function for reset filter
  const resetFilterData = () => {
    setIsFilterReset(true);
    setJsonFilterQuery([]);
    setReloadData(true);
  };

  // function to bulk stop automations
  const bulkStopAutomation = async () => {
    if (selectedEventIds.length === 0) {
      console.warn("No events selected for bulk stop.");
      return;
    }

    setIsBulkStopping(true);

    try {
      const requestUrl = `${url.API_BASE_URL}${url.API_AUTOMATION_EVENTS_STOP}`;

      console.log("eventIds:", selectedEventIds);

      const response = await postData(requestUrl, { eventIds: selectedEventIds });

      if (response.status) {
        console.log("Bulk stop successful:", response.data);
        setReloadData(true);
      } else {
        console.error("Error stopping automation events:", response.message);
      }

      setIsBulkStopping(false);
      
    } catch (error) {
      console.error("Error stopping automation events:", error);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchAutomationEvents();
  }, []);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: '#',
        header: t('Actions'),
        Cell: ({ row }) => (
          <Link
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#viewAutomationEvent"
            aria-controls="viewAutomationEvent"
            onClick={() => {
              setSelectedEventId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </Link>
        ),
        enableColumnActions: false, // Hides the column action icon
        enableColumnDragging: false, // Hides the move icon
        enableSorting: false,
      },
      {
        accessorKey: 'automation.name',
        header: t('Automation Name'),
        Cell: ({ row }) => (
          <p>
            <span className="d-block">{row.original.automation.name}</span>
            <span style={{ minWidth: 60, color: "#888" }}>{row.original.automation.code} </span>
          </p>
        ),
      },
      {
        accessorKey: "componentid",
        header: t("Component"),
        Cell: ({ row }) => {
          if(row.original.leadid) {
            return (
                  <div>
                    <div>{row.original.lead.name}</div>
                    <div className="text-muted">{row.original.lead.email}</div>
                  </div>);
          }

          if (row.original.userid) {
            return (
              <div>
                <div>{row.original.user.name}</div>
                <div className="text-muted">{row.original.user.email}</div>
              </div>
            );
          }

          if(row.original.contactid) {
            let contactName = '';
            let contactEmail = '';
            if(row.original.contactowner && row.original.contactowner.id != row.original.automation.owner) {
              contactName = row.original.contactowner?.name;
              contactEmail = row.original.contactowner?.email;
            }else{
              contactName = row.original.contactmember?.name;
              contactEmail = row.original.contactmember?.email;
            }
            return (
              <div>
                <div>{contactName}</div>
                <div className="text-muted">{contactEmail}</div>
              </div>
            );
          }



        },
      },
      {
        accessorKey: "automation.steps",
        header: t("Automation Steps"),
        Cell: ({ row }) => {
          const steps = row.original.automation?.steps || [];
          return (
            <div>
              {steps.map((step, index) => {
                const matchedAction = actionOptions.find(option => option.value === step.action);
                return (
                  <div key={index}>
                    <strong>{t("Step")} {step.order}:</strong> {matchedAction ? matchedAction.label : step.action}
                  </div>
                );
              })}
            </div>
          );
        }
      },
      {
        accessorKey: "execution.stepscompleted",
        header: t("Steps Executed"),
        Cell: ({ row }) => {
          const stepscompleted = row.original.execution?.stepscompleted || [];
          return (
            <div>
              {stepscompleted.map((step, index) => {
                const matchedAction = actionOptions.find(option => option.value === step.action);
                return (
                  <div key={index}>
                  <strong>{t("Step")} {step.order}:</strong> {matchedAction ? matchedAction.label : step.action}
                  <div className="text-muted">
                    {new Date(step.createdAt).toLocaleDateString() || t("N/A")} {new Date(step.createdAt).toLocaleTimeString() || t("N/A")}
                  </div>
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        accessorKey: "tagstring",
        header: t("Label"),
        Cell: ({ row }) => (
          <div className="label border-bottom-0">
            {row.original.tagstring != "" && (
              <ul className="d-flex flex-wrap gap-2 fs-xs">
                {row.original.tagstring.split(" , ").map((tag, index) => {
                  return (
                    <li
                      key={index}
                      className="px-2 py-1 gradient-light rounded-5"
                    >
                      {tag}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ),
      },
      {
        accessorKey: "automation.trigger",
        header: t("Condition"),
        Cell: ({ row }) => {
          const module = row.original.automation?.trigger?.module?.name?? t("N/A");
          const triggerType = row.original.automation?.trigger?.type?? t("N/A");

          const tag = row.original.automation?.trigger?.tag?.title?? t("N/A");

          const mailtemplate = row.original.automation?.trigger?.messagetemplate?? null;
          let mailtemplateName = '';
          let linkTrackerLabel = '';
          let linkTrackerLink = '';
          if(triggerType === "mail_linktracker_label_added" && mailtemplate) {
            mailtemplateName = mailtemplate.name;

            // Find the selected link tracker
            const selectedLinkTracker = mailtemplate.linktrackerids?.find(
              (tracker) => tracker._id === row.original.automation.trigger.value
            );

            if (selectedLinkTracker) {
              linkTrackerLabel = selectedLinkTracker.label;
              linkTrackerLink = selectedLinkTracker.link;
            }
          }
          const matchedType = typeOptions.find(option => option.value === triggerType);
            return (
            <div>
              <div><strong>{t("Module")}:</strong> {module}</div>
              <div><strong>{t("Type")}:</strong> {matchedType?matchedType.label:triggerType}</div>
              {triggerType === "mail_linktracker_label_added" ? (
                <>
                  <div><strong>{t("Mail Template")}:</strong> {mailtemplateName}</div>
                  <div><strong>{t("Link Tracker Label")}:</strong> {linkTrackerLabel}</div>
                  <div><strong>{t("Link Tracker Link")}:</strong> <a href={linkTrackerLink} target="_blank" rel="noopener noreferrer">{linkTrackerLink}</a></div>
                </>
              ) : (
                <div><strong>{t("Tag")}:</strong> {tag}</div>
              )}
              
            </div>
            );
        }
      },
      
      {
        accessorKey: "execution.stepsfailed",
        header: t("Steps Failed"),
        Cell: ({ row }) => {
          const stepsfailed = row.original.execution?.stepsfailed || [];
          return (
            <div>
              {stepsfailed.map((step, index) => {
                const matchedAction = actionOptions.find(option => option.value === step.action);
                return (
                  <div key={index}>
                    <strong>{t("Step")} {step.order}:</strong> {matchedAction ? matchedAction.label : step.action}
                  </div>
                );
              })}
            </div>
          );
        }
      },
      
      {
        accessorKey: "execution.executionstatus",
        header: t("Status"),
        Cell: ({ row }) => (
          <div>
            <span>
              {row.original.status == "2" ? (
                <span style={{ color: "red", fontWeight: "bold" }}>STOPPED</span>
              ) : (
                row.original.execution.executionstatus || t("N/A")
              )}
            </span>
            <div className="text-muted">
              {row.original.status == "1" && row.original.execution.executionstatus != "completed" ? t("Current Step") : t("Last Step")}:
              {row.original.execution?.currentsteporder??' N/A'}
            </div>
          </div>
          
        ),
      },
      {
        accessorKey: "createdAt",
        header: t("Date"),
        Cell: ({ row }) => (
          <div>
            <span>{new Date(row.original.createdAt).toLocaleDateString() || t("N/A")}</span>
            <div className="text-muted"> {new Date(row.original.createdAt).toLocaleTimeString() || t("N/A")}</div>
          </div>
        ),
      },

    ],
    [t]
  );

  //initialize the column order
  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ]; //array of column ids (Initializing is optional as of v2.10.0)

  const refreshRecords = () => {
    // Logic to refresh the records
    setJsonFilterQuery({});
    setReloadData(true);

  };

  useEffect(() => {
    if (reloadData) {
      fetchAutomationEvents();
      setReloadData(false);
    }
  }, [reloadData]);

  //
  useEffect(() => {
      if (jsonFilterQuery.length > 0) {

        console.log("Applying filter query:", jsonFilterQuery);

        fetchAutomationEvents();
      }
    }, [jsonFilterQuery]);

  // set selected events --------------
  useEffect(() => {
      const selectedIdsArray = Object.keys(rowSelection).filter(
        (key) => rowSelection[key]
      );
      if (selectedIdsArray.length > 0) {
        setSelectedEventIds(selectedIdsArray);
      } else {
        setSelectedEventIds([]);
      }
    }, [rowSelection]);

  return (
    <div id="content_wrapper">
      <section className="events-wrapper bg-white pb-5">
        <TabsHeader
              commonHeaderObject={commonHeaderObject}
              activeOption={t("Automation Events")}
            />

        <div className="container-fluid px-lg-5">

          <AutomationEventsHeader reloadList={refreshRecords} />

          {isLoading ? (
            <div className="placeholder-glow d-flex flex-column gap-4">
              <span className="placeholder placeholder-lg bg-secondary col-12"></span>
              <span className="placeholder placeholder-lg bg-secondary col-8"></span>
              <span className="placeholder placeholder-lg bg-secondary col-4"></span>
            </div>
          ) : (
            <>
            <div className="table-wrapper">
              <MaterialReactTable
                columns={columns}
                data={eventsList}
                enableGrouping
                  enableRowSelection // enable showing checkbox
                  getRowId={(row) => row._id} // map which value to select with row checkbox
                  onRowSelectionChange={setRowSelection} //connect internal row selection state to your own
                  state={{ rowSelection, columnOrder }} //pass our managed row selection state to the table to use
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 200,
                    size: 50, //make columns wider by default
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
              />
            </div>
            
            

            </>
            


          )}
        </div>
      </section>

      {/* ==== View Event Popup ==== */}
            <ViewEventsPopup
              automationEventId={selectedEventId}
            />

            {/* ==== modals and popup section ==== */}
            <EventFilterPopup
              setJsonFilterQuery={setJsonFilterQuery}
              reloadList={resetFilterData}
              isFilterReset={isFilterReset}
              setIsFilterReset={setIsFilterReset}
            />

            {/* add label modal */}
            <AddLabelModal
              moduleName="automationevent"
              selectedIds={selectedEventIds}
              afterTagModalClose={fetchAutomationEvents}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setMessageType={setMessageType}
            />
            {/* ----------- bulk automaton stop warning modal ------------ */}
            <div
                className="modal fade"
                id="bulkAutomationStopWarning"
                tabIndex="-1"
                aria-labelledby="bulkAutomationStopWarningLabel"
                aria-hidden="true"
                data-bs-backdrop="static"
              >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content bg-white border-0 rounded-15">
                  <div className="modal-body text-center px-4 py-5">
                    <img
                      src={assetImages.exclamationIcon}
                      alt="warning"
                      className="img-fluid"
                    />
                    <h2>
                      {t(`Are you sure you want to stop all the selected automation events?`)}
                    </h2>
                    {/* <h3>** Task with logs can't be deleted</h3> */}
                    <div className="d-flex gap-3 justify-content-center">
                      <Link
                        to="#"
                        className="btn btn-outline-primary"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                        onClick={isBulkStopping ? undefined : bulkStopAutomation}
                        disabled={isBulkStopping}
                        style={isBulkStopping ? { pointerEvents: "none", opacity: 0.6 } : {}}
                      >
                        {isBulkStopping ? (
                          // Show a spinner or loading placeholder
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : null}
                        {isBulkStopping ? t("Stopping...") : t("Yes Stop Selected")}
                      </Link>
        
                      <Link
                        to="#"
                        className="btn btn-primary"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                        disabled={isBulkStopping}
                        style={isBulkStopping ? { pointerEvents: "none", opacity: 0.6 } : {}}
                      >
                        {t("No, Cancel")}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>


    </div>
  );
};

export default AutomationEventsListBody;
